/* Documents & approval: the agency's own papers and each ambulance's, and
 * whether dispatch can send the agency work.
 *
 * Everything here is GET /agency/compliance — the admin's own read of this
 * agency (onboarding.AgencyCompliance, recomputed on read), so the agency sees
 * the same checklist, the same refusals and the same per-service answer the
 * platform team sees. Uploads are the existing multipart submissions
 * (POST /agency/documents, /agency/vehicles/{id}/documents): one part named
 * "file" (PDF, JPEG or PNG — the server reads the bytes, not the name). An
 * identity paper is recorded by its last four characters only; any other paper
 * needs the number printed on it. */

import { useMemo, useState } from 'react';
import {
  getCompliance,
  submitAgencyDocument,
  submitVehicleDocument,
  type ChecklistItem,
  type Compliance,
  type DocState,
  type DocumentFields,
  type DocumentRow,
  type DocumentType,
} from '../data/agency';
import { dateOnly, dateTime } from '../data/format';
import { errorText, serviceName } from '../data/words';
import { AGENCY_STATUS_WORD, canManage } from '../nav';
import { useProfile } from '../shell/profile';
import {
  Btn,
  Card,
  CardHead,
  CellStack,
  Drawer,
  DrawerBody,
  DrawerFoot,
  DrawerHead,
  Empty,
  Field,
  Kpi,
  KpiGrid,
  Note,
  Stack,
  Table,
  Tag,
  TRow,
  uiCss,
} from '../ui';
import { ServiceDot, useAsync, useNotice } from './common';

const BLOCKED_WORDS: Record<string, string> = {
  AGENCY_NOT_ACTIVE: 'Your agency is not approved yet.',
  MISSING_DOCUMENT: 'A required paper is missing.',
  EXPIRED_DOCUMENT: 'A required paper has expired.',
  NO_SERVICE_AREA: 'No service area is set for this city.',
  SERVICE_NOT_OFFERED: 'This service is not switched on.',
  SERVICE_NOT_IN_CITY: 'The platform does not run this service in your city.',
};

// A checklist entry's state is the server's DocumentState (onboarding/
// readiness_view.go): CURRENT is a paper in force — the word an uploaded
// paper's own row says as APPROVED. Both read "Approved" here.
const STATE: Record<string, { label: string; fg: string; bg: string }> = {
  CURRENT: { label: 'Approved', fg: 'var(--ok)', bg: 'var(--ok-bg)' },
  APPROVED: { label: 'Approved', fg: 'var(--ok)', bg: 'var(--ok-bg)' },
  PENDING_REVIEW: { label: 'Waiting for review', fg: '#2E6FC4', bg: '#E5F0FB' },
  PENDING: { label: 'Waiting for review', fg: '#2E6FC4', bg: '#E5F0FB' },
  MISSING: { label: 'Missing', fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  REJECTED: { label: 'Not accepted', fg: 'var(--danger)', bg: 'var(--danger-bg)' },
  EXPIRED: { label: 'Expired', fg: 'var(--danger)', bg: 'var(--danger-bg)' },
  SUPERSEDED: { label: 'Replaced', fg: 'var(--ink-faint)', bg: 'var(--surface-field)' },
};

function StateTag({ state }: { state: string }) {
  const s = STATE[state] ?? { label: state.replace(/_/g, ' ').toLowerCase(), fg: 'var(--ink-soft)', bg: 'var(--surface-field)' };
  return (
    <Tag fg={s.fg} bg={s.bg}>
      {s.label}
    </Tag>
  );
}

const TRIGGER_WORDS: Record<string, string> = {
  ALWAYS: 'Always needed',
  HAS_DRUG_LICENCE_EQUIPMENT: 'Needed because you stock licensed equipment',
  HAS_VEHICLES: 'Needed because you run an ambulance',
};

type Target = { kind: 'AGENCY' } | { kind: 'VEHICLE'; id: string; label: string };
type Upload = { target: Target; typeCode: string | null };

const CHECK_COLS = '1.8fr 1fr 1fr 0.9fr 0.8fr';
const DOC_COLS = '1.6fr 1fr 1fr 0.9fr 1fr';
const ELIG_COLS = '1.2fr 0.6fr 0.9fr 2fr';

export function DocumentsScreen() {
  const { profile, reload: reloadProfile } = useProfile();
  const manage = canManage(profile.me.role);
  const comp = useAsync(getCompliance, []);
  const { notice, setNotice } = useNotice();
  const [upload, setUpload] = useState<Upload | null>(null);

  const c = comp.data;
  const types = useMemo(() => new Map((c?.document_types ?? []).map((t) => [t.code, t])), [c]);
  const name = (code: string) => types.get(code)?.name ?? code.replace(/_/g, ' ').toLowerCase();
  const offered = new Set(c?.services_offered ?? []);
  const elig = (c?.eligibility ?? []).filter((e) => !e.equipment_type_code && offered.has(e.service_code));
  const needed = (c?.checklist ?? []).filter((x) => x.trigger_met);
  const missing = needed.filter((x) => x.is_mandatory && x.document.state !== 'CURRENT');
  const status = AGENCY_STATUS_WORD[profile.agency.status] ?? { label: profile.agency.status, tone: 'neutral' };

  const done = (msg: string) => {
    setUpload(null);
    setNotice({ tone: 'ok', text: msg });
    comp.reload();
    void reloadProfile();
  };

  return (
    <Stack>
      <KpiGrid>
        <Kpi
          label="Approval"
          value={status.label}
          note={profile.agency.status === 'ACTIVE' ? 'the platform approved your agency' : 'the platform team checks your agency'}
          color={profile.agency.status === 'ACTIVE' ? 'var(--ok)' : 'var(--amber)'}
        />
        <Kpi label="Papers still needed" value={c ? missing.length : '—'} note="missing, waiting or not accepted" color={missing.length ? 'var(--amber)' : undefined} />
        <Kpi
          label="Services ready"
          value={c ? `${elig.filter((e) => e.is_dispatchable).length} of ${elig.length}` : '—'}
          note="dispatch can send you requests for these"
        />
        <Kpi label="Ambulances" value={c ? c.vehicles.length : '—'} note="each needs its own papers" />
      </KpiGrid>

      {notice}
      {comp.error ? <Note tone="danger">Your papers could not be read: {comp.error}</Note> : null}
      {!manage ? <Note tone="amber">Only an owner or admin can upload documents. You can read them.</Note> : null}
      {profile.agency.status === 'REJECTED' && profile.agency.rejection_reason ? (
        <Note tone="danger">The platform did not approve your agency: {profile.agency.rejection_reason}</Note>
      ) : null}

      {/* ------------------------------------------------------------ dispatch */}
      <Card>
        <CardHead
          title="Can dispatch send you work?"
          sub={`The platform's own answer for each service you offer${c ? `, checked ${dateOnly(c.as_of)}` : ''}. A service is ready when your agency is approved, an area is set and every paper it needs is approved.`}
        />
        {!c ? (
          <Empty>{comp.loading ? 'Reading…' : '—'}</Empty>
        ) : elig.length === 0 ? (
          <Empty>No service is switched on yet. Choose your services under Services & area.</Empty>
        ) : (
          <Table cols={ELIG_COLS} head={['Service', 'City', 'Status', 'Why not']} minWidth={640}>
            {elig.map((e) => (
              <TRow key={`${e.service_code}-${e.city_code}`} cols={ELIG_COLS}>
                <span style={{ font: '700 13px var(--font-ui)' }}>
                  <ServiceDot code={e.service_code} />
                  {serviceName(e.service_code)}
                </span>
                <span className={uiCss.sub}>{e.city_code}</span>
                <span>
                  {e.is_dispatchable ? (
                    <Tag fg="var(--ok)" bg="var(--ok-bg)">
                      Ready
                    </Tag>
                  ) : (
                    <Tag fg="var(--amber)" bg="var(--amber-bg)">
                      Not yet
                    </Tag>
                  )}
                </span>
                <span className={uiCss.sub}>
                  {e.is_dispatchable
                    ? '—'
                    : [
                        BLOCKED_WORDS[e.blocked_reason ?? ''] ?? (e.blocked_reason ? e.blocked_reason.replace(/_/g, ' ').toLowerCase() : 'Not ready.'),
                        e.blocking_document_type_code ? name(e.blocking_document_type_code) : null,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                </span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>

      {/* ------------------------------------------------------------ checklist */}
      <Card>
        <CardHead
          title="Papers your agency needs"
          sub="What the platform asks of an agency offering your services. Upload each one; the platform team checks it."
          right={
            manage ? (
              <Btn size="sm" onClick={() => setUpload({ target: { kind: 'AGENCY' }, typeCode: null })}>
                Upload another paper
              </Btn>
            ) : null
          }
        />
        {!c ? (
          <Empty>{comp.loading ? 'Reading…' : '—'}</Empty>
        ) : needed.length === 0 ? (
          <Empty>Nothing is asked of you yet. Papers appear here once you switch a service on.</Empty>
        ) : (
          <Table cols={CHECK_COLS} head={['Paper', 'Needed', 'State', 'Valid until', '']} minWidth={760}>
            {needed.map((x) => (
              <ChecklistRow key={`${x.document_type_code}-${x.service_code ?? ''}`} x={x} name={name} manage={manage} onUpload={() => setUpload({ target: { kind: 'AGENCY' }, typeCode: x.document_type_code })} />
            ))}
          </Table>
        )}
      </Card>

      {/* ------------------------------------------------------------ everything sent */}
      <Card>
        <CardHead title="Everything you have sent" sub="Every agency paper you uploaded, newest first, and what became of it." />
        {!c ? (
          <Empty>{comp.loading ? 'Reading…' : '—'}</Empty>
        ) : c.documents.length === 0 ? (
          <Empty>No paper uploaded yet.</Empty>
        ) : (
          <DocTable rows={c.documents} name={name} />
        )}
      </Card>

      {/* ------------------------------------------------------------ vehicles */}
      <Card>
        <CardHead title="Ambulance papers" sub="Each ambulance's registration, insurance, fitness, permit and pollution papers." />
        {!c ? (
          <Empty>{comp.loading ? 'Reading…' : '—'}</Empty>
        ) : c.vehicles.length === 0 ? (
          <Empty>No ambulances on your team. A driver who joins you with their own ambulance brings it here.</Empty>
        ) : (
          <div>
            {c.vehicles.map((v) => (
              <div key={v.public_id} style={{ borderTop: '1px solid var(--line-soft)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px' }}>
                  <span className={uiCss.grow}>
                    <span className={uiCss.monoStrong}>{v.registration_number}</span>
                    <span className={uiCss.sub}>
                      {' '}
                      · {v.documents.filter((d) => d.status === 'APPROVED').length} approved · {v.documents.length} sent
                    </span>
                  </span>
                  {manage ? (
                    <Btn size="sm" onClick={() => setUpload({ target: { kind: 'VEHICLE', id: v.public_id, label: v.registration_number }, typeCode: null })}>
                      Upload a paper
                    </Btn>
                  ) : null}
                </div>
                {v.documents.length ? <DocTable rows={v.documents} name={name} /> : <Empty>No paper uploaded for this ambulance yet.</Empty>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {upload && c ? (
        <UploadDrawer
          upload={upload}
          compliance={c}
          agencyPublicId={profile.agency.public_id}
          onClose={() => setUpload(null)}
          onDone={done}
        />
      ) : null}
    </Stack>
  );
}

function ChecklistRow({ x, name, manage, onUpload }: { x: ChecklistItem; name: (c: string) => string; manage: boolean; onUpload: () => void }) {
  const d: DocState = x.document;
  return (
    <TRow cols={CHECK_COLS}>
      <CellStack
        top={<strong>{name(x.document_type_code)}</strong>}
        sub={[x.service_code ? `for ${serviceName(x.service_code)}` : null, x.blocks_dispatch ? 'blocks requests until approved' : null].filter(Boolean).join(' · ') || undefined}
      />
      <CellStack top={x.is_mandatory ? 'Required' : 'Optional'} sub={TRIGGER_WORDS[x.trigger_condition] ?? undefined} />
      <CellStack
        top={<StateTag state={d.state} />}
        sub={
          d.state === 'REJECTED' && d.rejection_reason
            ? d.rejection_reason
            : x.pending_renewal
              ? `Renewal ${STATE[x.pending_renewal.state]?.label.toLowerCase() ?? x.pending_renewal.state.toLowerCase()}`
              : undefined
        }
      />
      <span className={uiCss.sub}>{d.expires_on ? dateOnly(d.expires_on) : '—'}</span>
      <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {manage ? (
          <Btn size="sm" variant={d.state === 'CURRENT' ? 'plain' : 'primary'} onClick={onUpload}>
            {d.state === 'MISSING' ? 'Upload' : d.state === 'CURRENT' ? 'Renew' : 'Replace'}
          </Btn>
        ) : null}
      </span>
    </TRow>
  );
}

function DocTable({ rows, name }: { rows: DocumentRow[]; name: (c: string) => string }) {
  return (
    <Table cols={DOC_COLS} head={['Paper', 'Number', 'State', 'Valid until', 'Sent']} minWidth={720}>
      {rows.map((d) => (
        <TRow key={d.public_id} cols={DOC_COLS}>
          <CellStack top={<strong>{name(d.document_type_code)}</strong>} sub={d.issuing_authority ? `issued by ${d.issuing_authority}` : undefined} />
          <span className={uiCss.mono}>{d.document_number || d.masked_value || '—'}</span>
          <CellStack top={<StateTag state={d.status} />} sub={d.status === 'REJECTED' && d.rejection_reason ? d.rejection_reason : d.file ? 'file attached' : 'no file'} />
          <span className={uiCss.sub}>{d.expires_on ? dateOnly(d.expires_on) : '—'}</span>
          <span className={uiCss.sub}>{dateTime(d.submitted_at)}</span>
        </TRow>
      ))}
    </Table>
  );
}

function UploadDrawer({
  upload,
  compliance,
  agencyPublicId,
  onClose,
  onDone,
}: {
  upload: Upload;
  compliance: Compliance;
  agencyPublicId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const subject = upload.target.kind;
  const options: DocumentType[] = compliance.document_types.filter((t) => t.subject_type === subject);
  const [type, setType] = useState(upload.typeCode ?? options[0]?.code ?? '');
  const [number, setNumber] = useState('');
  const [last4, setLast4] = useState('');
  const [authority, setAuthority] = useState('');
  const [issued, setIssued] = useState('');
  const [expires, setExpires] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const meta = options.find((t) => t.code === type);
  const identity = !!meta?.is_identity_document;

  const blocked = !type
    ? 'Choose the paper.'
    : identity && !/^[A-Za-z0-9]{4}$/.test(last4.trim())
      ? 'Enter the last four characters of the number.'
      : !identity && !number.trim()
        ? 'Enter the number printed on the paper.'
        : meta?.has_expiry && !expires
          ? 'This paper has an expiry date. Enter it.'
          : !file
            ? 'Choose the file to upload.'
            : null;

  const send = async () => {
    if (blocked || !file) return;
    const fields: DocumentFields = {
      document_type_code: type,
      document_number: identity ? undefined : number.trim(),
      masked_value: identity ? `XXXX${last4.trim().toUpperCase()}` : undefined,
      issuing_authority: authority,
      issued_on: issued,
      expires_on: expires,
    };
    setBusy(true);
    setError(null);
    try {
      if (upload.target.kind === 'AGENCY') await submitAgencyDocument(agencyPublicId, fields, file);
      else await submitVehicleDocument(upload.target.id, fields, file);
      onDone(`${meta?.name ?? 'The paper'} was uploaded. It is waiting for the platform team's check.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open onClose={onClose} label="Upload a paper">
      <DrawerHead onClose={onClose}>
        <div className={uiCss.label}>{upload.target.kind === 'AGENCY' ? 'Agency paper' : `Ambulance ${upload.target.label}`}</div>
        <div style={{ font: '700 19px/1.25 var(--font-display)', marginTop: 4 }}>{meta?.name ?? 'Upload a paper'}</div>
        {meta?.description ? <div className={uiCss.cardSub}>{meta.description}</div> : null}
      </DrawerHead>
      <DrawerBody>
        <Stack>
          {error ? <Note tone="danger">{error}</Note> : null}
          <Field label="Paper">
            <select className={uiCss.input} value={type} onChange={(e) => setType(e.target.value)}>
              {options.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {identity ? (
            <Field label="Last four characters of the number">
              <input className={uiCss.input} value={last4} maxLength={4} onChange={(e) => setLast4(e.target.value)} placeholder="1234" />
            </Field>
          ) : (
            <Field label="Number printed on the paper">
              <input className={uiCss.input} value={number} maxLength={120} onChange={(e) => setNumber(e.target.value)} />
            </Field>
          )}
          <Field label="Issued by (optional)">
            <input className={uiCss.input} value={authority} maxLength={200} onChange={(e) => setAuthority(e.target.value)} />
          </Field>
          <div className={uiCss.formGrid}>
            <Field label="Issued on (optional)">
              <input className={uiCss.input} type="date" value={issued} onChange={(e) => setIssued(e.target.value)} />
            </Field>
            <Field label={meta?.has_expiry ? 'Valid until' : 'Valid until (if any)'}>
              <input className={uiCss.input} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
            </Field>
          </div>
          <Field label="File — PDF, JPEG or PNG">
            <input className={uiCss.input} type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Field>
          {identity ? <Note>An identity paper is recorded by its last four characters only. The full number stays on the file.</Note> : null}
        </Stack>
      </DrawerBody>
      <DrawerFoot>
        <Btn onClick={onClose}>Cancel</Btn>
        <div style={{ flex: 1 }} />
        <Btn variant="primary" disabled={busy || !!blocked} title={blocked ?? undefined} onClick={() => void send()}>
          {busy ? 'Uploading…' : 'Upload'}
        </Btn>
      </DrawerFoot>
    </Drawer>
  );
}
